import { DestroyRef, Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult
} from '@azure/msal-browser';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { decodeJwtPayload } from './jwt-payload.util';
import { getAzureAdConfig, isAzureAdEnabled } from './azure-ad-env';

/** Client-only header: prevents infinite 401 → refresh → retry loops in {@link azureAdApiInterceptor}. */
export const MSAL_ID_TOKEN_RETRY_HEADER = 'x-retry';

/**
 * Thrown (via `throwError`) when MSAL starts **`acquireTokenRedirect`** so callers can distinguish
 * redirect from generic failures. {@link azureAdApiInterceptor} emits one HTTP response whose body is
 * {@link MSAL_REDIRECT_HTTP_BODY} (see {@link isMsalRedirectResponse}).
 */
export const MSAL_REDIRECT_ERROR_MESSAGE = 'MSAL_REDIRECT';

export function isMsalRedirectError(err: unknown): boolean {
  return err instanceof Error && err.message === MSAL_REDIRECT_ERROR_MESSAGE;
}

/** Synthetic JSON body when the real API request is short-circuited for MSAL redirect. */
export const MSAL_REDIRECT_HTTP_BODY = { __msalRedirect: true } as const;

export type MsalRedirectHttpBody = typeof MSAL_REDIRECT_HTTP_BODY;

/** `true` when `res` is the sentinel from {@link MSAL_REDIRECT_HTTP_BODY} (e.g. after `firstValueFrom`). */
export function isMsalRedirectResponse(res: unknown): res is MsalRedirectHttpBody {
  return (
    typeof res === 'object' &&
    res !== null &&
    (res as Record<string, unknown>)['__msalRedirect'] === true
  );
}

/**
 * Cross-tab signal: other tabs {@link MsalTokenService} listen for `storage` on this key and clear
 * their in-memory token share cache so they reuse MSAL browser cache without duplicate refresh.
 */
export const MSAL_TOKEN_UPDATED_STORAGE_KEY = 'msal.token.updated';

/** Renew `id_token` this long before JWT `exp` (default path uses cache when outside this window). */
const ID_TOKEN_EXPIRY_LEAD_MS = 2 * 60 * 1000;

/**
 * Centralized MSAL acquisition for SPICE when the **backend authorizes with `id_token`**.
 *
 * Uses **`loginScopes` only** (via {@link getAzureAdConfig} / `environment.azureAd.loginScopes`).
 * Default flow uses **`acquireTokenSilent` without `forceRefresh`**; **`forceRefresh: true`** only when
 * the cached `id_token` is missing / near expiry, or when {@link getAccessToken} is called with
 * `{ forceRefresh: true }` (e.g. HTTP 401 retry in {@link azureAdApiInterceptor}).
 *
 * On silent failure when **interaction is required** ({@link InteractionRequiredAuthError} or
 * `errorCode === 'interaction_required'`): **`acquireTokenRedirect`** runs and the stream errors with
 * {@link MSAL_REDIRECT_ERROR_MESSAGE} so {@link azureAdApiInterceptor} can complete with a synthetic
 * **204** body ({@link MSAL_REDIRECT_HTTP_BODY}) for stable `firstValueFrom` / async flows. Other failures
 * are logged and propagated.
 *
 * Parallel and **rapid sequential** **`getAccessToken()`** calls (no `forceRefresh`) share **one**
 * MSAL chain via `shareReplay(1)` plus a short TTL. The cached stream is only reused while the
 * last emitted **`id_token`** is still within {@link ID_TOKEN_EXPIRY_LEAD_MS} of expiry so
 * `shareReplay` cannot serve an expired JWT past the JWT window.
 *
 * Simultaneous **`getAccessToken({ forceRefresh: true })`** calls (e.g. many 401 retries) share **one**
 * forced silent acquire via {@link #refreshInProgress$}.
 *
 * Cross-tab: when {@link getAccessToken} emits a token whose lightweight fingerprint
 * (`exp` + signature tail) differs from {@link #lastTokenFingerprint}, this tab sets
 * {@link MSAL_TOKEN_UPDATED_STORAGE_KEY} in **`localStorage`**; other tabs clear
 * {@link #clearDefaultTokenShareCache} on the **`storage`** event.
 */
@Injectable({ providedIn: 'root' })
export class MsalTokenService {
  private readonly msal = inject(MsalService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.registerCrossTabTokenSyncListener();
  }

  /** Reuse the same shared default-token observable within this window (ms). */
  private static readonly TOKEN_SHARE_CACHE_TTL_MS = 2000;

  /** Shared stream for default {@link getAccessToken} deduplication. */
  private inFlightToken$?: Observable<string>;

  /** When {@link #inFlightToken$} was created; used with {@link TOKEN_SHARE_CACHE_TTL_MS}. */
  private tokenCacheTimestamp = 0;

  /** Last `id_token` string emitted by the default shared stream (for JWT reuse checks). */
  private lastToken: string | undefined;

  /** Last emitted JWT `exp` (Unix seconds), aligned with the current fingerprint. */
  private lastTokenExp?: number;

  /** `exp` (normalized) + last 16 chars of JWT — detects rotation within the same `exp` second. */
  private lastTokenFingerprint?: string;

  /** In-flight forced refresh so parallel 401 retries do not each call `acquireTokenSilent(true)`. */
  private refreshInProgress$?: Observable<string>;

  /**
   * Full MSAL result for proactive refresh scheduling (`expiresOn` / id_token exp in logs).
   * Uses cache when `id_token` is still valid; force-refreshes only when near expiry.
   */
  acquireTokenSilentForApi(): Observable<AuthenticationResult> {
    return this.acquireLoginScopeSilent$(false).pipe(
      switchMap((result) => {
        const id = typeof result.idToken === 'string' ? result.idToken.trim() : '';
        if (!id || this.isJwtExpired(id, ID_TOKEN_EXPIRY_LEAD_MS)) {
          return this.acquireLoginScopeSilent$(true);
        }
        return of(result);
      }),
      tap((r) => this.logTokenExpiryIfDev(r))
    );
  }

  /**
   * **`id_token`** for `Authorization: Bearer …`.
   * @param options.forceRefresh — when `true`, clears the default share cache and runs (or joins) a
   *   single forced `acquireTokenSilent` for parallel 401 retries.
   */
  getAccessToken(options?: { forceRefresh?: boolean }): Observable<string> {
    if (options?.forceRefresh === true) {
      this.clearDefaultTokenShareCache();
      if (this.refreshInProgress$) {
        return this.refreshInProgress$;
      }
      this.refreshInProgress$ = this.acquireLoginScopeSilent$(true).pipe(
        map((r) => this.emitTokenIfChanged(this.requireIdToken(r))),
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => {
          this.refreshInProgress$ = undefined;
        })
      );
      return this.refreshInProgress$;
    }

    const now = Date.now();
    const withinTtl =
      this.inFlightToken$ != null &&
      now - this.tokenCacheTimestamp < MsalTokenService.TOKEN_SHARE_CACHE_TTL_MS;
    if (withinTtl && this.inFlightToken$) {
      const jwtStillUsable =
        this.lastToken == null ||
        this.lastToken === '' ||
        !this.isJwtExpired(this.lastToken, ID_TOKEN_EXPIRY_LEAD_MS);
      if (jwtStillUsable) {
        return this.inFlightToken$;
      }
      this.clearDefaultTokenShareCache();
    }

    this.clearDefaultTokenShareCache();
    this.tokenCacheTimestamp = Date.now();
    this.inFlightToken$ = this.getAccessTokenDefault$().pipe(
      map((token) => this.emitTokenIfChanged(token)),
      shareReplay(1)
    );
    return this.inFlightToken$;
  }

  private clearDefaultTokenShareCache(): void {
    this.inFlightToken$ = undefined;
    this.tokenCacheTimestamp = 0;
    this.lastToken = undefined;
    this.lastTokenExp = undefined;
    this.lastTokenFingerprint = undefined;
  }

  /** JWT `exp` claim as Unix seconds, or `undefined` if missing / not parseable. */
  private jwtExpSecFromToken(token: string): number | undefined {
    const payload = decodeJwtPayload(token);
    if (!payload) {
      return undefined;
    }
    const exp = payload['exp'];
    if (typeof exp === 'number' && Number.isFinite(exp)) {
      return exp;
    }
    if (typeof exp === 'string' && /^\d+$/.test(exp.trim())) {
      return Number(exp.trim());
    }
    return undefined;
  }

  /**
   * Updates {@link #lastToken}, {@link #lastTokenExp}, and {@link #lastTokenFingerprint}; notifies other
   * tabs when the fingerprint changes (same-second token rotation included).
   */
  private emitTokenIfChanged(token: string): string {
    const exp = this.jwtExpSecFromToken(token);
    const expKey = exp != null ? String(exp) : '';
    const tail = token.length <= 16 ? token : token.slice(-16);
    const fingerprint = `${expKey}:${tail}`;
    const isNew = this.lastTokenFingerprint !== fingerprint;
    this.lastTokenFingerprint = fingerprint;
    this.lastTokenExp = exp;
    this.lastToken = token;
    if (isNew) {
      this.notifyOtherTabsTokenRefreshed();
    }
    return token;
  }

  private registerCrossTabTokenSyncListener(): void {
    if (typeof window === 'undefined' || !isAzureAdEnabled()) {
      return;
    }
    const key = MSAL_TOKEN_UPDATED_STORAGE_KEY;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key || event.storageArea !== localStorage) {
        return;
      }
      this.clearDefaultTokenShareCache();
    };
    window.addEventListener('storage', onStorage);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', onStorage));
  }

  /** Notifies other browser tabs (this tab does not receive `storage`). */
  private notifyOtherTabsTokenRefreshed(): void {
    if (typeof window === 'undefined' || !isAzureAdEnabled()) {
      return;
    }
    try {
      localStorage.setItem(MSAL_TOKEN_UPDATED_STORAGE_KEY, String(Date.now()));
    } catch {
      /* private mode / quota — cross-tab sync is best-effort */
    }
  }

  /** Default token path: silent cache, then force refresh only if `id_token` missing or near expiry. */
  private getAccessTokenDefault$(): Observable<string> {
    return this.acquireLoginScopeSilent$(false).pipe(
      switchMap((result) => {
        const id = typeof result.idToken === 'string' ? result.idToken.trim() : '';
        if (!id) {
          return this.acquireLoginScopeSilent$(true).pipe(map((r) => this.requireIdToken(r)));
        }
        if (this.isJwtExpired(id, ID_TOKEN_EXPIRY_LEAD_MS)) {
          return this.acquireLoginScopeSilent$(true).pipe(map((r) => this.requireIdToken(r)));
        }
        return of(id);
      })
    );
  }

  /**
   * `true` when JWT `exp` is within `skewMs` of now or already past (`skewMs` treats token as “stale early”).
   */
  isJwtExpired(token: string, skewMs = 60_000): boolean {
    const expSec = this.jwtExpSecFromToken(token);
    if (expSec == null) {
      return true;
    }
    return Date.now() >= expSec * 1000 - skewMs;
  }

  private getLoginScopesForIdToken(): string[] {
    const ad = getAzureAdConfig();
    const fromEnv = ad?.loginScopes?.filter((s) => typeof s === 'string' && s.trim().length > 0);
    if (fromEnv != null && fromEnv.length > 0) {
      return fromEnv;
    }
    return ['User.Read'];
  }

  private requireIdToken(result: AuthenticationResult): string {
    const id = typeof result.idToken === 'string' ? result.idToken.trim() : '';
    if (!id) {
      throw new Error('MSAL did not return an id_token; backend expects id_token for authorization.');
    }
    return id;
  }

  /**
   * Ensures MSAL **active account** is set (e.g. after refresh or multi-tab) by falling back to
   * the first cached account from **getAllAccounts** and calling **setActiveAccount**.
   */
  tryEnsureMsalActiveAccount(): boolean {
    if (!this.msal) {
      return false;
    }
    try {
      this.ensureActiveAccount(this.msal);
      return true;
    } catch {
      return false;
    }
  }

  private ensureActiveAccount(msal: MsalService): AccountInfo {
    let account = msal.instance.getActiveAccount();
    if (!account) {
      const accounts = msal.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0]!;
        msal.instance.setActiveAccount(account);
      }
    }
    if (!account) {
      throw new Error('No active account found');
    }
    return account;
  }

  private acquireLoginScopeSilent$(forceRefresh: boolean): Observable<AuthenticationResult> {
    if (!isAzureAdEnabled() || !this.msal) {
      return throwError(() => new Error('MSAL is not available'));
    }
    const msal = this.msal;
    if (getAzureAdConfig() == null) {
      return throwError(() => new Error('Azure AD is not configured'));
    }
    let account: AccountInfo;
    try {
      account = this.ensureActiveAccount(msal);
    } catch (err) {
      return throwError(() => (err instanceof Error ? err : new Error(String(err))));
    }
    const scopes = this.getLoginScopesForIdToken();

    return from(
      msal.instance.acquireTokenSilent({
        scopes,
        account,
        forceRefresh
      })
    ).pipe(
      catchError((error: unknown) => {
        if (this.isMsalInteractionRequired(error)) {
          console.warn('[MSAL] Interaction required, redirecting', error);
          void msal.instance.acquireTokenRedirect({
            scopes,
            account
          });
          return throwError(() => new Error(MSAL_REDIRECT_ERROR_MESSAGE));
        }
        console.error('[MSAL] Silent token acquisition failed (non-interaction issue)', error);
        return throwError(() => error);
      })
    );
  }

  /** True when Entra requires interactive auth (consent/login), not for generic network failures. */
  private isMsalInteractionRequired(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    if (typeof error === 'object' && error !== null && 'errorCode' in error) {
      const code = String((error as { errorCode?: string }).errorCode ?? '').toLowerCase();
      return code === 'interaction_required';
    }
    return false;
  }

  private logTokenExpiryIfDev(result: AuthenticationResult): void {
    if (environment.production) {
      return;
    }
    const at = result.expiresOn?.toISOString?.();
    if (at) {
      console.debug('[MSAL] Token cache expiresOn:', at);
    }
    const idt = result.idToken;
    if (typeof idt === 'string' && idt.split('.').length === 3) {
      const payload = decodeJwtPayload(idt);
      const expRaw = payload?.['exp'];
      const expSec =
        typeof expRaw === 'number' && Number.isFinite(expRaw)
          ? expRaw
          : typeof expRaw === 'string' && /^\d+$/.test(expRaw.trim())
            ? Number(expRaw.trim())
            : NaN;
      if (Number.isFinite(expSec)) {
        console.debug('[MSAL] id_token exp:', new Date(expSec * 1000).toISOString());
        if (this.isJwtExpired(idt, 0)) {
          console.warn('[MSAL] id_token exp is already in the past after silent acquire (clock skew?)');
        }
      }
    }
  }
}
